import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import { createVote, removeVote, clearVoteError } from "../../redux/Slices/reviewSlice";

export interface Vote {
  id: string;
  userId: string;
  voteType: "helpful" | "unhelpful";
  createdAt: Date;
}

export interface ReplyVoteButtonProps {
  reviewId: string;
  hasReply: boolean;
  votes?: Vote[];
  currentUserId?: string;
  className?: string;
}

const ReplyVoteButton: React.FC<ReplyVoteButtonProps> = ({
  reviewId,
  hasReply,
  votes = [],
  currentUserId,
  className = ""
}) => {
  const dispatch = useAppDispatch();
  const { voteLoading, voteError } = useAppSelector((state) => state.reviews);
  const [userVote, setUserVote] = useState<"helpful" | "unhelpful" | null>(null);
  const [localVotes, setLocalVotes] = useState<Vote[]>(votes);

  // Update local votes when props change
  useEffect(() => {
    setLocalVotes(votes);
  }, [votes]);

  // Find user's current vote
  useEffect(() => {
    if (currentUserId && localVotes.length > 0) {
      const userCurrentVote = localVotes.find(vote => vote.userId === currentUserId);
      setUserVote(userCurrentVote?.voteType || null);
    } else if (!currentUserId) {
      setUserVote(null);
    }
  }, [currentUserId, localVotes]);

  const handleVote = async (voteType: "helpful" | "unhelpful") => {
    if (voteLoading || !currentUserId) return;

    try {
      // Clear any previous errors
      dispatch(clearVoteError());
      
      // Optimistic update - add the vote locally
      const newVote: Vote = {
        id: `temp-${Date.now()}`, // Temporary ID
        userId: currentUserId,
        voteType: voteType === "helpful" ? "helpful" : "unhelpful",
        createdAt: new Date()
      };
      
      // Remove any existing vote from this user first
      const updatedVotes = localVotes.filter(vote => vote.userId !== currentUserId);
      // Add the new vote
      setLocalVotes([...updatedVotes, newVote]);
      
      // Convert vote type to API format
      const apiVoteType = voteType === "helpful" ? "helpful" : "not_helpful";
      
      const result = await dispatch(createVote({ 
        reviewId, 
        data: { voteType: apiVoteType } 
      })).unwrap();
      
      // Log the result for debugging
      console.log("Reply vote created successfully:", result);
    } catch (error) {
      console.error("Error submitting reply vote:", error);
      // Revert optimistic update on error
      setLocalVotes(votes);
    }
  };

  const handleRemoveVote = async () => {
    if (voteLoading || !currentUserId) return;

    try {
      // Clear any previous errors
      dispatch(clearVoteError());
      
      // Optimistic update - remove the vote locally
      const updatedVotes = localVotes.filter(vote => vote.userId !== currentUserId);
      setLocalVotes(updatedVotes);
      
      const result = await dispatch(removeVote(reviewId)).unwrap();
      
      // Log the result for debugging
      console.log("Reply vote removed successfully:", result);
    } catch (error) {
      console.error("Error removing reply vote:", error);
      // Revert optimistic update on error
      setLocalVotes(votes);
    }
  };

  const handleYesClick = () => {
    if (userVote === "helpful") {
      // If user already voted helpful, remove the vote
      handleRemoveVote();
    } else {
      // Vote helpful
      handleVote("helpful");
    }
  };

  const handleNoClick = () => {
    if (userVote === "unhelpful") {
      // If user already voted unhelpful, remove the vote
      handleRemoveVote();
    } else {
      // Vote unhelpful
      handleVote("unhelpful");
    }
  };

  if (!hasReply) return null;

  return (
    <div className={`mt-2 flex items-center gap-2 ${className}`}>
      <span className="text-xs text-blue-600">Was this response helpful?</span>
      <button
        onClick={handleYesClick}
        disabled={voteLoading || !currentUserId}
        className={`
          text-xs font-medium px-3 py-1 rounded-full transition-all duration-200
          ${userVote === "helpful" 
            ? "bg-green-100 text-green-700 border border-green-300" 
            : "text-blue-600 hover:text-green-600 hover:bg-green-50"
          }
          ${voteLoading || !currentUserId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
      >
        {userVote === "helpful" ? "✓ Yes" : "Yes"}
      </button>
      <button
        onClick={handleNoClick}
        disabled={voteLoading || !currentUserId}
        className={`
          text-xs font-medium px-3 py-1 rounded-full transition-all duration-200
          ${userVote === "unhelpful" 
            ? "bg-red-100 text-red-700 border border-red-300" 
            : "text-blue-600 hover:text-red-600 hover:bg-red-50"
          }
          ${voteLoading || !currentUserId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
      >
        {userVote === "unhelpful" ? "✓ No" : "No"}
      </button>
      
      {/* Loading indicator */}
      {voteLoading && (
        <div className="flex items-center text-gray-500 text-xs">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500 mr-1"></div>
          Updating...
        </div>
      )}
      
      {/* Error display */}
      {voteError && (
        <span className="text-xs text-red-500">{voteError}</span>
      )}
    </div>
  );
};

export default ReplyVoteButton;

